import React, { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { mutate } from 'swr';
import { useFormat } from 'helpers/hooks/useFormat';
import { Reference, ReferenceLink } from 'helpers/reference';
import { useAccount } from 'frontastic';
import Image from 'frontastic/lib/image';

const LoginModal = dynamic(() => import('../aafes/Modals/Login/login'));

type Props = {
  pageTitle?: string;
  image?: { media: any } | any;
  title?: string;
  subtitle?: string;
  callToAction?: string;
  callToActionLink?: Reference;
};

export const EmptyState: React.FC<Props> = ({
  pageTitle,
  image,
  title,
  subtitle,
  callToAction,
  callToActionLink,
}: Props) => {
  const { loggedIn } = useAccount();
  const [loginModal, setLoginModal] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState('/cart');
  const router = useRouter();
  const { formatMessage } = useFormat({ name: 'cart' });
  const isCartPage = router?.asPath === redirectUrl;
  const [account, setAccount] = useState<boolean | null>(null);

  const getAccountData = async () => {
    try {
      const data = await mutate('/action/account/getAccount');
      return data;
    } catch (error) {
      console.error('Error fetching account data:', error);
    }
  };

  useEffect(() => {
    const fetchAccountData = async () => {
      const accountData = await getAccountData();
      setAccount(accountData?.loggedIn);
      if (!loggedIn && !accountData?.loggedIn && pageTitle === 'Shopping Cart' && isCartPage) {
        openLoginModal();
        setRedirectUrl('/cart');
      }
    };

    fetchAccountData();
  }, []);

  const openLoginModal = () => setLoginModal(true);
  const closeLoginModal = () => setLoginModal(false);

  return (
    <div className="mx-auto max-w-2xl px-2 pt-16 pb-24 sm:px-4 lg:max-w-7xl lg:px-8">
      {image && (
        <div className="grid justify-items-center">
          <div className="mt-20 h-32 w-32">
            <Image
              media={image.media ? image.media : { media: '' }}
              src={!image.media ? image : ''}
              className="h-7 w-auto sm:h-10"
              alt="Empty Wishlist"
            />
          </div>
        </div>
      )}

      {title && (
        <div className="mt-4 text-center">
          <h1 className="text-3xl font-bold text-gray-600 dark:text-light-100">{title}</h1>
        </div>
      )}

      {subtitle && (
        <div className="mt-2 w-auto text-center">
          <h1 className="text-lg text-gray-600 dark:text-light-100">
            {!loggedIn && pageTitle === 'Shopping Cart' && isCartPage && !account
              ? formatMessage({
                  id: 'cartLoginMsg',
                  defaultMessage: 'Please log-in to view the contents of your cart',
                })
              : subtitle}
          </h1>
        </div>
      )}
      {callToActionLink && (
        <div className="mt-8 mb-24 text-center">
          {!loggedIn && pageTitle === 'Shopping Cart' && isCartPage && !account ? (
            <button
              className="w-56 rounded bg-accent-400 py-3 px-4 font-bold text-white hover:bg-accent-500"
              onClick={openLoginModal}
            >
              {formatMessage({ id: 'cartLogin', defaultMessage: 'Log in to Cart' })}
            </button>
          ) : (
            <ReferenceLink target={callToActionLink}>
              <button className="w-56 rounded bg-accent-400 py-3 px-4 font-bold text-white hover:bg-accent-500">
                {callToAction}
              </button>
            </ReferenceLink>
          )}
        </div>
      )}

      <LoginModal
        loginModal={loginModal}
        closeModal={closeLoginModal}
        closeAccountFlyout={closeLoginModal}
        loginCallBack={() => router.push(redirectUrl)}
      />
    </div>
  );
};
